import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Card, Row, Col, Button, Table, Badge, Alert, 
  Spinner, Form, InputGroup, Modal 
} from "react-bootstrap";
import axios from "axios";
import Footer from "../layouts/Footer";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

export default function ImportDetails() {
  const { bulkId } = useParams();
  const [importDetails, setImportDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // États pour les modals
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Récupération des détails de l'import
  useEffect(() => {
    const fetchImportDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("Token d'authentification manquant");
        }


        const response = await apiClient.get(`/bulk-detail?bulk_id=${bulkId}`, {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        const data = response.data;
    

        if (!data.data || !Array.isArray(data.data)) {
          throw new Error("Structure de données invalide");
        }

        setImportDetails(data.data);

      } catch (err) {
        console.error("❌ Erreur chargement détails:", err);
        setError(`Erreur: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (bulkId) {
      fetchImportDetails();
    }
  }, [bulkId]);

  // Fonction pour exécuter les actions (validate, cancel, delete)
  const handleAction = async (action) => {
    try {
      setActionLoading(true);
      
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Token d'authentification manquant");
      }

      const payload = {
        bulk_id: bulkId,
        action: action
      };

      // Ajouter la date pour la validation
      if (action === 'validate' && selectedDate) {
        const dateFormatted = selectedDate.replace('T', ' ') + ':00';
        payload.date_envoi = dateFormatted;
      }

   

      const response = await apiClient.put('/update-import', payload, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      // Fermer les modals
      setShowValidateModal(false);
      setShowCancelModal(false);
      setShowDeleteModal(false);
      setSelectedDate("");

      // Recharger les données
      window.location.reload();

    } catch (err) {
      console.error(`❌ Erreur action ${action}:`, err);
      setError(`Erreur lors de l'action: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Filtrage des données
  const filteredItems = importDetails.filter(item =>
    item.message?.toLowerCase().includes(filterText.toLowerCase()) ||
    item.msisdn?.includes(filterText) ||
    item.status?.toLowerCase().includes(filterText.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  // Statistiques des statuts
  const getStatusStats = () => {
    const total = importDetails.length;
    const sent = importDetails.filter(item => item.status === '200').length;
    const pending = importDetails.filter(item => ['201', '202', '403', '500', '404'].includes(item.status)).length;
    const successRate = total > 0 ? ((sent / total) * 100).toFixed(1) : 0;

    return {
      total,
      sent,
      pending,
      successRate,
      imported: importDetails.filter(item => item.status === '201').length,
      validated: importDetails.filter(item => item.status === '202').length,
      invalid: importDetails.filter(item => item.status === '404').length,
      cancelled: importDetails.filter(item => item.status === '400').length,
      error: importDetails.filter(item => item.status === '500').length,
      unknown: importDetails.filter(item => !['201', '202', '200', '400', '500', '404'].includes(item.status)).length
    };
  };

  const statusStats = getStatusStats();

  // Fonction pour obtenir le badge de statut
  const getStatusBadge = (status) => {
    const statusMap = {
      '201': { bg: 'info', text: 'Importé', icon: 'bi-download' },
      '202': { bg: 'primary', text: 'Validé', icon: 'bi-check-circle' },
      '200': { bg: 'success', text: 'Envoyé', icon: 'bi-send' },
      '400': { bg: 'warning', text: 'Annulé', icon: 'bi-x-circle' },
      '204': { bg: 'danger', text: 'Supprimé', icon: 'bi-x-circle' },
      '404': { bg: 'secondary', text: 'Invalide', icon: 'bi-x-circle' },
      '403': { bg: 'secondary', text: 'Erreur Accès', icon: 'bi-x-circle' },
      '500': { bg: 'dark', text: 'Erreur serveur', icon: 'bi-exclamation-triangle' }
    };

    const statusInfo = statusMap[status] || { 
      bg: 'secondary', 
      text: status || 'Inconnu', 
      icon: 'bi-question-circle' 
    };
    
    return (
      <Badge bg={statusInfo.bg} className="d-flex align-items-center" style={{ width: 'fit-content' }}>
        <i className={`${statusInfo.icon} me-1`}></i>
        {statusInfo.text}
      </Badge>
    );
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Non traité';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR');
    } catch {
      return dateString;
    }
  };

  const isAllImportedStatus = () => {
    return importDetails.length > 0 && 
           importDetails.every(item => item.status === '201');
  };

  return (
    <div className="main main-app p-3 p-lg-4">
      <div className="container-fluid">
        {/* En-tête */}
        <Row className="mb-4">
          <Col md={12}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <Button 
                  variant="outline-primary" 
                  as={Link} 
                  to="/apps/EnvoieMasseHome"
                  className="mb-3"
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour aux imports
                </Button>
                <h4 className="mb-1 fw-bold">Détails de l'import</h4>
                <p className="text-muted mb-0">
                  ID: <code>{bulkId}</code> • {importDetails.length} messages
                </p>
              </div>
              <div className="d-flex gap-2">
                <Badge bg="info" className="fs-6">
                  <i className="bi bi-info-circle me-1"></i>
                  {importDetails.length} messages
                </Badge>
              </div>
            </div>
          </Col>
        </Row>

        {/* Cartes de statistiques en haut */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 bg-primary text-white shadow-sm">
              <Card.Body className="text-center">
                <i className="bi bi-envelope display-6 mb-2"></i>
                <h3 className="mb-1">{statusStats.total}</h3>
                <p className="mb-0">SMS Total</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 bg-success text-white shadow-sm">
              <Card.Body className="text-center">
                <i className="bi bi-send-check display-6 mb-2"></i>
                <h3 className="mb-1">{statusStats.sent}</h3>
                <p className="mb-0">SMS Envoyés</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 bg-warning text-white shadow-sm">
              <Card.Body className="text-center">
                <i className="bi bi-clock display-6 mb-2"></i>
                <h3 className="mb-1">{statusStats.pending}</h3>
                <p className="mb-0">SMS En Attente</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 bg-info text-white shadow-sm">
              <Card.Body className="text-center">
                <i className="bi bi-graph-up display-6 mb-2"></i>
                <h3 className="mb-1">{statusStats.successRate}%</h3>
                <p className="mb-0">Taux de Succès</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Indicateurs de chargement et erreur */}
        {error && (
          <Row className="mb-3">
            <Col md={12}>
              <Alert variant="danger" className="d-flex justify-content-between align-items-center">
                <div>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  onClick={() => window.location.reload()}
                >
                  Réessayer
                </Button>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Tableau des détails */}
        <Row>
          <Col md={12}>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
                <div className="d-flex align-items-center">
                  <i className="bi bi-list-ul me-2 fs-5"></i>
                  <span className="fw-semibold">Messages de l'import</span>
                </div>
                <div className="d-flex gap-3 flex-wrap">
                  {isAllImportedStatus() && (
                    <>
                      <Button 
                        variant="success" 
                        onClick={() => setShowValidateModal(true)}
                        disabled={actionLoading}
                        className="d-flex align-items-center"
                      >
                        <i className="bi bi-check-circle me-2"></i>
                        Valider l'import
                      </Button>
                      <Button 
                        variant="warning" 
                        onClick={() => setShowCancelModal(true)}
                        disabled={actionLoading}
                        className="d-flex align-items-center"
                      >
                        <i className="bi bi-x-circle me-2"></i>
                        Annuler l'import
                      </Button>
                      <Button 
                        variant="danger" 
                        onClick={() => setShowDeleteModal(true)}
                        disabled={actionLoading}
                        className="d-flex align-items-center"
                      >
                        <i className="bi bi-trash me-2"></i>
                        Supprimer l'import
                      </Button>
                    </>
                  )}
                </div>
              </Card.Header>
              
              <Card.Body className="p-0">
                {/* Barre de recherche */}
                <div className="p-3 border-bottom bg-light">
                  <Row>
                    <Col md={6}>
                      <InputGroup>
                        <InputGroup.Text>
                          <i className="bi bi-search"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher dans les messages..."
                          value={filterText}
                          onChange={(e) => {
                            setFilterText(e.target.value);
                            setCurrentPage(1);
                          }}
                        />
                        {filterText && (
                          <Button 
                            variant="outline-secondary" 
                            onClick={() => setFilterText('')}
                          >
                            <i className="bi bi-x"></i>
                          </Button>
                        )}
                      </InputGroup>
                    </Col>
                    <Col md={6} className="text-end">
                      <small className="text-muted">
                        Page {currentPage} sur {totalPages} • 
                        Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredItems.length)} sur {filteredItems.length} messages
                      </small>
                    </Col>
                  </Row>
                </div>

                <div className="table-responsive">
                  <Table className="table-hover mb-0 table-striped">
                    <thead className="bg-secondary text-white">
                      <tr>
                        <th width="15%">NUMÉRO</th>
                        <th width="45%">MESSAGE</th>
                        <th width="15%">STATUT</th>
                        <th width="15%">DATE IMPORT</th>
                        <th width="10%">DATE ENVOI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="text-center py-5">
                            <Spinner animation="border" variant="primary" className="mb-3" />
                            <p className="mt-3 text-muted mb-0">Chargement des détails en cours...</p>
                          </td>
                        </tr>
                      ) : currentItems.length > 0 ? (
                        currentItems.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <code className="fw-bold">{item.msisdn}</code>
                            </td>
                            <td>
                              <div className="fw-medium" title={item.message}>
                                {item.message?.length > 80 
                                  ? `${item.message.substring(0, 80)}...` 
                                  : item.message
                                }
                              </div>
                            </td>
                            <td>
                              {getStatusBadge(item.status)}
                            </td>
                            <td>
                              <small className="text-muted">
                                {formatDate(item.date_import)}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {formatDate(item.date_envoi)}
                              </small>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-5">
                            <i className="bi bi-inbox display-4 text-muted"></i>
                            <h5 className="mt-3 text-muted">Aucun message trouvé</h5>
                            {filterText ? (
                              <>
                                <p className="text-muted mb-3">Aucun résultat pour "{filterText}"</p>
                                <Button 
                                  variant="outline-primary"
                                  onClick={() => setFilterText('')}
                                >
                                  Réinitialiser la recherche
                                </Button>
                              </>
                            ) : (
                              <p className="text-muted mb-3">
                                Aucun message dans cet import ou erreur de chargement
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                  <div className="p-3 border-top d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredItems.length)} sur {filteredItems.length} messages
                    </div>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                      >
                        <i className="bi bi-chevron-double-left"></i>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </Button>
                      
                      {[...Array(Math.min(5, totalPages))].map((_, index) => {
                        const pageNum = currentPage <= 3 
                          ? index + 1 
                          : currentPage >= totalPages - 2 
                            ? totalPages - 4 + index 
                            : currentPage - 2 + index;
                        
                        if (pageNum > 0 && pageNum <= totalPages) {
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "primary" : "outline-secondary"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </Button>
                          );
                        }
                        return null;
                      })}
                      
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        <i className="bi bi-chevron-double-right"></i>
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* MODAL DE VALIDATION */}
      <Modal 
        show={showValidateModal} 
        onHide={() => setShowValidateModal(false)}
        centered
        size="lg"
      >
        <Modal.Header className="bg-success text-white border-0">
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-check-circle-fill me-3 fs-4"></i>
            <div>
              <h5 className="mb-0">Valider l'import</h5>
              <small className="opacity-75">Confirmer la programmation de l'envoi</small>
            </div>
          </Modal.Title>
          <Button 
            variant="link" 
            className="btn-close btn-close-white" 
            onClick={() => setShowValidateModal(false)}
          />
        </Modal.Header>
        
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
              <i className="bi bi-calendar-check text-success fs-1"></i>
            </div>
            <h5 className="text-success">Programmer l'envoi des messages</h5>
            <p className="text-muted">
              Vous êtes sur le point de valider cet import pour envoi. 
              Veuillez sélectionner la date et l'heure d'envoi souhaitées.
            </p>
          </div>

          <Alert variant="info" className="d-flex align-items-center">
            <i className="bi bi-info-circle me-2 fs-5"></i>
            <div>
              <strong>Import ID:</strong> <code>{bulkId}</code> • 
              <strong> Messages:</strong> {importDetails.length}
            </div>
          </Alert>

          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">
              <i className="bi bi-clock me-2"></i>
              Date et heure d'envoi
            </Form.Label>
            <Form.Control
              type="datetime-local"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              required
              className="form-control-lg"
            />
            <Form.Text className="text-muted">
              Sélectionnez la date et l'heure auxquelles les messages doivent être envoyés
            </Form.Text>
          </Form.Group>

          <div className="bg-light rounded p-3">
            <h6 className="fw-semibold mb-3">Récapitulatif de l'import:</h6>
            <Row>
              <Col md={6}>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">SMS total:</span>
                  <strong>{statusStats.total}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">En attente:</span>
                  <strong className="text-warning">{statusStats.pending}</strong>
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Déjà envoyés:</span>
                  <strong className="text-success">{statusStats.sent}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Taux de succès:</span>
                  <strong className="text-info">{statusStats.successRate}%</strong>
                </div>
              </Col>
            </Row>
          </div>
        </Modal.Body>
        
        <Modal.Footer className="border-0 bg-light">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowValidateModal(false)}
            disabled={actionLoading}
            className="px-4"
          >
            <i className="bi bi-arrow-left me-2"></i>
            Retour
          </Button>
          <Button 
            variant="success" 
            onClick={() => handleAction('validate')}
            disabled={actionLoading || !selectedDate}
            className="px-4"
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Programmation...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Programmer l'envoi
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL D'ANNULATION */}
      <Modal 
        show={showCancelModal} 
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header className="bg-warning text-dark border-0">
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
            <div>
              <h5 className="mb-0">Annuler l'import</h5>
              <small className="opacity-75">Action irréversible</small>
            </div>
          </Modal.Title>
          <Button 
            variant="link" 
            className="btn-close" 
            onClick={() => setShowCancelModal(false)}
          />
        </Modal.Header>
        
        <Modal.Body className="p-4 text-center">
          <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
            <i className="bi bi-x-circle text-warning fs-1"></i>
          </div>
          <h5 className="text-warning mb-3">Confirmer l'annulation</h5>
          <p className="text-muted mb-4">
            Êtes-vous sûr de vouloir annuler cet import ? 
            <br />
            <strong className="text-warning">Cette action est irréversible.</strong>
          </p>

          <Alert variant="warning" className="text-start">
            <div className="d-flex">
              <i className="bi bi-info-circle me-2 mt-1"></i>
              <div>
                <strong>Conséquences de l'annulation:</strong>
                <ul className="mb-0 mt-2">
                  <li>Tous les messages en attente seront annulés</li>
                  <li>Les statistiques seront mises à jour</li>
                  <li>L'action ne peut pas être annulée</li>
                </ul>
              </div>
            </div>
          </Alert>
        </Modal.Body>
        
        <Modal.Footer className="border-0 bg-light justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowCancelModal(false)}
            disabled={actionLoading}
            className="px-4"
          >
            <i className="bi bi-arrow-left me-2"></i>
            Retour
          </Button>
          <Button 
            variant="warning" 
            onClick={() => handleAction('cancel')}
            disabled={actionLoading}
            className="px-4"
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Annulation...
              </>
            ) : (
              <>
                <i className="bi bi-x-circle me-2"></i>
                Confirmer l'annulation
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL DE SUPPRESSION */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header className="bg-danger text-white border-0">
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-trash-fill me-3 fs-4"></i>
            <div>
              <h5 className="mb-0">Supprimer l'import</h5>
              <small className="opacity-75">Action définitive</small>
            </div>
          </Modal.Title>
          <Button 
            variant="link" 
            className="btn-close btn-close-white" 
            onClick={() => setShowDeleteModal(false)}
          />
        </Modal.Header>
        
        <Modal.Body className="p-4 text-center">
          <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
            <i className="bi bi-exclamation-octagon text-danger fs-1"></i>
          </div>
          <h5 className="text-danger mb-3">Attention ! Suppression définitive</h5>
          
          <Alert variant="danger" className="text-start">
            <div className="d-flex">
              <i className="bi bi-exclamation-triangle me-2 mt-1 fs-5"></i>
              <div>
                <strong>Cette action ne peut pas être annulée</strong>
                <ul className="mb-0 mt-2">
                  <li>Toutes les données de cet import seront définitivement supprimées</li>
                  <li>Les statistiques et historiques seront perdus</li>
                  <li>Cette action est irréversible</li>
                </ul>
              </div>
            </div>
          </Alert>

          <div className="bg-light rounded p-3 mt-3">
            <p className="text-muted mb-2">
              <strong>Import à supprimer:</strong> <code>{bulkId}</code>
            </p>
            <p className="text-muted mb-0">
              <strong>Nombre de messages:</strong> {importDetails.length}
            </p>
          </div>

          <p className="text-muted mt-3">
            Êtes-vous absolument sûr de vouloir supprimer cet import ?
          </p>
        </Modal.Body>
        
        <Modal.Footer className="border-0 bg-light justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={actionLoading}
            className="px-4"
          >
            <i className="bi bi-arrow-left me-2"></i>
            Annuler
          </Button>
          <Button 
            variant="danger" 
            onClick={() => handleAction('delete')}
            disabled={actionLoading}
            className="px-4"
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Suppression...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                Supprimer définitivement
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </div>
  );
}